// Design System MagicLoader Component
// Custom animated loading component with magical lamp theme

import React from 'react';
import { Box, Typography, styled } from '@mui/material';
import { keyframes } from '@mui/system';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens } from '../../tokens';

// Fallback colors if tokens aren't loaded
const fallbackGoldColor = '#FFD859';
const fallbackNavyColor = '#0F3A61';

// Define animation keyframes
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const sparkle = keyframes`
  0% { opacity: 0; transform: scale(0.4) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
  100% { opacity: 0; transform: scale(0.4) rotate(360deg); }
`;

// Styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  width: '100%',
  position: 'relative',
}));

const LampContainer = styled(Box)(({ theme, size }) => ({
  position: 'relative',
  width: size === 'small' ? 60 : size === 'medium' ? 80 : 120,
  height: size === 'small' ? 80 : size === 'medium' ? 100 : 140,
  animation: `${floatAnimation} 3s ease-in-out infinite`,
}));

const Lamp = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 120'%3E%3Cpath d='M30,100 L70,100 L65,80 L35,80 Z' fill='%23D9AD1F'/%3E%3Cpath d='M35,80 L65,80 L60,65 L40,65 Z' fill='%23F2C526'/%3E%3Cpath d='M40,65 L60,65 C70,55 75,40 75,30 C75,15 60,5 50,5 C40,5 25,15 25,30 C25,40 30,55 40,65 Z' fill='%23FFD859'/%3E%3Cpath d='M38,100 L62,100 L64,110 L36,110 Z' fill='%23B08E1A'/%3E%3C/svg%3E")`,
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  zIndex: 2,
}));

const LampGlow = styled(Box)(({ theme, color }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '120%',
  height: '120%',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${color || (colorTokens?.base?.gold?.[500] || fallbackGoldColor)}80 0%, rgba(255,255,255,0) 70%)`,
  opacity: 0.5,
  animation: `${pulse} 2s ease-in-out infinite`,
  zIndex: 1,
}));

const MagicSmoke = styled(Box)(({ theme, color }) => ({
  position: 'absolute',
  top: '10%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '80%',
  height: '50%',
  background: `radial-gradient(circle, ${color || (colorTokens?.base?.navy?.[700] || fallbackNavyColor)}60 0%, rgba(255,255,255,0) 70%)`,
  borderRadius: '50%',
  filter: 'blur(15px)',
  zIndex: 0,
  opacity: 0.6,
  animation: `${pulse} 4s ease-in-out infinite`,
}));

const MagicCircle = styled(Box)(({ theme, size, color }) => ({
  position: 'absolute',
  top: '0%',
  left: '50%',
  transform: 'translateX(-50%)',
  width: size === 'small' ? 100 : size === 'medium' ? 150 : 200,
  height: size === 'small' ? 30 : size === 'medium' ? 40 : 50,
  borderRadius: '50%',
  border: `2px solid ${color || (colorTokens?.base?.gold?.[500] || fallbackGoldColor)}`,
  opacity: 0.7,
  zIndex: 3,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-4px',
    left: '-4px',
    right: '-4px',
    bottom: '-4px',
    borderRadius: '50%',
    border: `1px dashed ${color || (colorTokens?.base?.gold?.[500] || fallbackGoldColor)}`,
    animation: `${rotate} 8s linear infinite`,
  },
}));

const SparkleEffect = styled(Box)(({ theme, index, color }) => ({
  position: 'absolute',
  width: '15px',
  height: '15px',
  top: `${20 + index * 20}%`,
  left: `${40 + (index % 2 === 0 ? 20 : -20)}%`,
  animation: `${sparkle} 3s ease-in-out ${index * 0.5}s infinite`,
  '&::before': {
    content: '"✧"',
    position: 'absolute',
    color: color || (colorTokens?.base?.gold?.[500] || fallbackGoldColor),
    fontSize: '14px',
    textShadow: `0 0 10px ${color || (colorTokens?.base?.gold?.[500] || fallbackGoldColor)}`,
  },
}));

const LoadingMessage = styled(Typography)(({ theme }) => ({
  marginTop: spacingTokens.layout.lg,
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  textAlign: 'center',
  maxWidth: '300px',
}));

/**
 * MagicLoader - Magical lamp-themed loading component
 * 
 * Features:
 * - Animated floating lamp
 * - Glowing effects
 * - Magic smoke animation
 * - Sparkle effects
 * - Customizable size and colors
 * - Loading message support
 * 
 * @example
 * // Basic usage
 * <MagicLoader />
 * 
 * // With message
 * <MagicLoader 
 *   message="Loading analytics data..." 
 *   size="large" 
 * />
 * 
 * // Custom colors
 * <MagicLoader
 *   color="#FF5722"
 *   smokeColor="#1976D2"
 *   showSparkles={false}
 * />
 */
const MagicLoader = ({
  size = 'medium',
  color,
  smokeColor,
  message,
  showLamp = true,
  showGlow = true,
  showSmoke = true,
  showCircle = true,
  showSparkles = true,
  sparkleCount = 3,
  variant = 'mystical',
  sx,
  ...props
}) => {
  return (
    <LoaderContainer sx={sx} {...props}>
      {showLamp && (
        <LampContainer size={size}>
          {showSmoke && <MagicSmoke color={smokeColor} />}
          {showGlow && <LampGlow color={color} />}
          <Lamp />
          {showCircle && <MagicCircle size={size} color={color} />}
          {showSparkles && [...Array(sparkleCount)].map((_, index) => (
            <SparkleEffect key={index} index={index} color={color} />
          ))}
        </LampContainer>
      )}
      
      {message && (
        <LoadingMessage variant="body2">
          {message}
        </LoadingMessage>
      )}
    </LoaderContainer>
  );
};

MagicLoader.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.string,
  smokeColor: PropTypes.string,
  message: PropTypes.string,
  showLamp: PropTypes.bool,
  showGlow: PropTypes.bool,
  showSmoke: PropTypes.bool,
  showCircle: PropTypes.bool,
  showSparkles: PropTypes.bool,
  sparkleCount: PropTypes.number,
  variant: PropTypes.oneOf(['mystical', 'simple']),
  sx: PropTypes.object,
};

export default MagicLoader;